'use client';

import { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import type { SchoolViewExtra, SchoolYear } from '@/types/api';
import { SchoolYearDialog } from '@/components/calendar-builder/school-year-dialog';
import { useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Shield,
  Palette,
  Calendar as CalendarIcon,
  Plus,
  X,
  Save,
  Building2,
  MapPin,
  Upload,
  Pencil,
  Check,
  Trash2,
  ChevronDown,
  Edit,
} from 'lucide-react';
import {
  useUpdateRotationPattern,
  useUpdateSchool,
  useUpdateScheduleType,
  useUpdateLunchSlots,
  useCreateLunchClass,
  useDeleteLunchClass,
  useCreateDayType,
  useUpdateDayType,
  useDeleteDayType,
  useCreatePeriodType,
  useUpdatePeriodType,
  useDeletePeriodType,
  useSchoolYears,
} from '@/hooks/use-schools';
import { toast } from 'sonner';
import {
  SCHOOL_STATUSES,
  LITE_LEVELS,
  COUNTRIES,
  getStateOptions,
  COLOR_SWATCHES,
  SCHEDULE_TYPES,
  LUNCH_TYPES,
  TIMEZONES,
} from '@/config/constants';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { validateLatitude, validateLongitude, validateImageFile, validateDuplicateNames } from '@/lib/validation';

interface SchoolSettingsTabProps {
  school: SchoolViewExtra;
  openSection?: string | null;
  onSectionOpened?: () => void;
}

// Helper function to get status badge variant (matching school-overview-tab)
function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'live':
      return 'default';
    case 'waitlist':
      return 'secondary';
    case 'suspended':
      return 'destructive';
    default:
      return 'secondary';
  }
}

export function SchoolSettingsTab({ school, openSection, onSectionOpened }: SchoolSettingsTabProps) {
  const schoolPrimaryColor = school.primaryColor || '#1e40af';
  const isCollege = school.organizationType === 'college';

  // Function to determine if white or black text should be used based on background color
  const getContrastColor = (hexColor: string | undefined): string => {
    if (!hexColor) return 'white';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  };

  // Basic Info State
  const [schoolTitle, setSchoolTitle] = useState(school.schoolTitle || '');
  const [nickname, setNickname] = useState(school.nickname || '');
  const [website, setWebsite] = useState(school.website || '');
  const [domains, setDomains] = useState<string[]>((school.domains as string[]) || []);
  const [newDomain, setNewDomain] = useState('');
  const [studentsCount, setStudentsCount] = useState(school.studentsCount ?? 0);
  const [status, setStatus] = useState(school.status || 'live');
  const [liteLevel, setLiteLevel] = useState(school.liteLevel || 'L1');
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    school.logo?.resourceUrl || null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Location State
  const [country, setCountry] = useState<'US' | 'CA'>((school.country as 'US' | 'CA') || 'US');
  const [state, setState] = useState(school.state || '');
  const [lat, setLat] = useState(school.lat?.toString() || '');
  const [lng, setLng] = useState(school.lng?.toString() || '');
  const [timezone, setTimezone] = useState(school.timezone || '');
  
  // Rotation pattern state
  const [localRotationPattern, setLocalRotationPattern] = useState<string[]>(
    school.rotationPattern || []
  );
  
  // General settings state
  const [isPrivate, setIsPrivate] = useState(school.private || false);
  const [alphabeticLunchWaves, setAlphabeticLunchWaves] = useState(
    school.alphabeticLunchWaves || false
  );
  
  // Appearance state
  const [primaryColor, setPrimaryColor] = useState(school.primaryColor || '');
  
  // Schedule type state
  const [scheduleType, setScheduleType] = useState(school.scheduleType || '');
  
  // Lunch settings state
  const [lunchType, setLunchType] = useState<'unit' | 'class' | 'waves' | undefined>(
    school.lunchSettings?.type
  );
  const [numLunchWaves, setNumLunchWaves] = useState(
    school.lunchSettings?.type === 'waves' ? school.lunchSettings.slots.length : 1
  );
  
  // Day and period type management state - local copies for edit mode
  const [localDayTypes, setLocalDayTypes] = useState<Array<{ id: string; name: string }>>(
    school.dayTypes || []
  );
  const [localPeriodTypes, setLocalPeriodTypes] = useState<Array<{ id: string; name: string }>>(
    school.periodTypes || []
  );
  const [editingDayTypeId, setEditingDayTypeId] = useState<string | null>(null);
  const [editingDayTypeName, setEditingDayTypeName] = useState('');
  const [newDayTypeName, setNewDayTypeName] = useState('');
  const [isAddingDayType, setIsAddingDayType] = useState(false);
  
  const [editingPeriodTypeId, setEditingPeriodTypeId] = useState<string | null>(null);
  const [editingPeriodTypeName, setEditingPeriodTypeName] = useState('');
  const [newPeriodTypeName, setNewPeriodTypeName] = useState('');
  const [isAddingPeriodType, setIsAddingPeriodType] = useState(false);
  
  // Track which day/period types to create/update/delete
  const [dayTypesToCreate, setDayTypesToCreate] = useState<string[]>([]);
  const [dayTypesToUpdate, setDayTypesToUpdate] = useState<Array<{ id: string; name: string }>>([]);
  const [dayTypesToDelete, setDayTypesToDelete] = useState<string[]>([]);
  const [periodTypesToCreate, setPeriodTypesToCreate] = useState<string[]>([]);
  const [periodTypesToUpdate, setPeriodTypesToUpdate] = useState<Array<{ id: string; name: string }>>([]);
  const [periodTypesToDelete, setPeriodTypesToDelete] = useState<string[]>([]);
  
  // School year management state
  const [showSchoolYearDialog, setShowSchoolYearDialog] = useState(false);
  const [editingSchoolYear, setEditingSchoolYear] = useState<SchoolYear | null>(null);
  
  // Collapsible section state
  const [openSections, setOpenSections] = useState({
    basicInfo: false,
    location: false,
    schoolYears: false,
    calendar: false,
    general: false,
    appearance: false,
    security: false,
    cohorts: false,
  });
  
  // Edit mode state for each section
  const [editMode, setEditMode] = useState({
    basicInfo: false,
    calendar: false,
    general: false,
  });
  
  const queryClient = useQueryClient();
  
  const updateRotationMutation = useUpdateRotationPattern(school.schoolName);
  const updateSchoolMutation = useUpdateSchool(school.schoolName, school.organizationType);
  const updateScheduleTypeMutation = useUpdateScheduleType(school.schoolName);
  const updateLunchSlotsMutation = useUpdateLunchSlots(school.schoolName);
  const createLunchClassMutation = useCreateLunchClass(school.schoolName);
  const deleteLunchClassMutation = useDeleteLunchClass(school.schoolName);
  
  const createDayTypeMutation = useCreateDayType(school.schoolName);
  const updateDayTypeMutation = useUpdateDayType(school.schoolName);
  const deleteDayTypeMutation = useDeleteDayType(school.schoolName);
  
  const createPeriodTypeMutation = useCreatePeriodType(school.schoolName);
  const updatePeriodTypeMutation = useUpdatePeriodType(school.schoolName);
  const deletePeriodTypeMutation = useDeletePeriodType(school.schoolName);
  
  // Fetch school years
  const {
    data: schoolYears,
    isLoading: isLoadingYears,
  } = useSchoolYears(school.schoolName);
  
  // Get lunch class ID if it exists
  const lunchClassId = school.courses?.find((c) => c.name === 'Lunch')?.id;

  // Update state options when country changes
  const stateOptions = getStateOptions(country);

  // Track initial country to avoid resetting state on first load
  const initialCountry = useRef((school.country as 'US' | 'CA') || 'US');

  // Reset state when country changes (but not on initial load)
  useEffect(() => {
    if (country !== initialCountry.current) {
      setState('');
    }
  }, [country]);

  // Handle opening a specific section
  useEffect(() => {
    if (openSection) {
      setOpenSections(prev => ({ ...prev, [openSection]: true }));
      onSectionOpened?.();
    }
  }, [openSection, onSectionOpened]);

  // Sync local day/period types when school data changes (e.g., after save)
  useEffect(() => {
    if (!editMode.calendar) {
      setLocalDayTypes(school.dayTypes || []);
      setLocalPeriodTypes(school.periodTypes || []);
    }
  }, [school.dayTypes, school.periodTypes, editMode.calendar]);

  // Handle logo file selection
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateImageFile(file);
      if (error) {
        toast.error(error);
        e.target.value = '';
        return;
      }
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Domain management
  const addDomain = () => {
    if (newDomain && !domains.includes(newDomain)) {
      setDomains([...domains, newDomain]);
      setNewDomain('');
    }
  };

  const removeDomain = (domain: string) => {
    setDomains(domains.filter((d) => d !== domain));
  };

  // Rotation pattern functions
  const addToRotation = (dayType: string) => {
    const newPattern = [...localRotationPattern, dayType];
    setLocalRotationPattern(newPattern);
  };

  const removeFromRotation = (index: number) => {
    const newPattern = localRotationPattern.filter((_, i) => i !== index);
    setLocalRotationPattern(newPattern);
  };

  const autofillRotation = () => {
    if (localDayTypes && localDayTypes.length > 0) {
      setLocalRotationPattern(localDayTypes.map((dt) => dt.name));
    }
  };

  // Section-specific save functions
  const saveBasicInfo = async () => {
    if (!schoolTitle.trim()) {
      toast.error('School title is required');
      return;
    }

    const parsedLat = lat ? parseFloat(lat) : undefined;
    const parsedLng = lng ? parseFloat(lng) : undefined;

    const latError = validateLatitude(parsedLat);
    if (latError) {
      toast.error(latError);
      return;
    }
    const lngError = validateLongitude(parsedLng);
    if (lngError) {
      toast.error(lngError);
      return;
    }

    if (state && !getStateOptions(country).some(s => s.value === state)) {
      toast.error(`Selected state/province is not valid for ${country === 'CA' ? 'Canada' : 'United States'}`);
      return;
    }

    try {
      const schoolUpdates: any = {
        schoolTitle,
        nickname: nickname || undefined,
        website: website || undefined,
        domains: domains as any,
        studentsCount,
        status,
        // Only include liteLevel when status is not 'live'
        ...(status !== 'live' && { liteLevel }),
        country: country || undefined,
        state: state || undefined,
        lat: parsedLat,
        lng: parsedLng,
        timezone: timezone || undefined,
      };

      await updateSchoolMutation.mutateAsync({
        school: schoolUpdates,
        logo: logo || undefined,
      });
      
      toast.success('Basic information saved successfully');
      setEditMode({ ...editMode, basicInfo: false });
      setLogo(null);
    } catch (error) {
      toast.error('Failed to save basic information');
    }
  };

  const saveCalendarSettings = async () => {
    const emptyDayType = localDayTypes.find(dt => !dt.name.trim());
    if (emptyDayType) {
      toast.error('Day type names cannot be empty');
      return;
    }
    const emptyPeriodType = localPeriodTypes.find(pt => !pt.name.trim());
    if (emptyPeriodType) {
      toast.error('Period type names cannot be empty');
      return;
    }
    const dupDayError = validateDuplicateNames(localDayTypes.map(dt => dt.name), 'day type');
    if (dupDayError) {
      toast.error(dupDayError);
      return;
    }
    const dupPeriodError = validateDuplicateNames(localPeriodTypes.map(pt => pt.name), 'period type');
    if (dupPeriodError) {
      toast.error(dupPeriodError);
      return;
    }

    try {
      const promises = [];
      
      // Save rotation pattern if changed
      if (hasRotationChanges) {
        promises.push(updateRotationMutation.mutateAsync(localRotationPattern));
      }
      
      // Save schedule type if changed
      if (hasScheduleTypeChanges && scheduleType) {
        promises.push(updateScheduleTypeMutation.mutateAsync(scheduleType));
      }
      
      // Handle lunch type changes
      const currentLunchType = school.lunchSettings?.type;
      if (lunchType !== currentLunchType) {
        switch (lunchType) {
          case 'unit':
            // Delete lunch class if exists, reset waves
            if (lunchClassId) {
              promises.push(deleteLunchClassMutation.mutateAsync(lunchClassId));
            }
            if (currentLunchType === 'waves') {
              promises.push(updateLunchSlotsMutation.mutateAsync(0));
            }
            break;
          case 'class':
            // Create lunch class, reset waves
            if (currentLunchType === 'waves') {
              promises.push(updateLunchSlotsMutation.mutateAsync(0));
            }
            if (!lunchClassId) {
              promises.push(createLunchClassMutation.mutateAsync());
            }
            break;
          case 'waves':
            // Delete lunch class if exists, set initial waves
            if (lunchClassId) {
              promises.push(deleteLunchClassMutation.mutateAsync(lunchClassId));
            }
            promises.push(updateLunchSlotsMutation.mutateAsync(numLunchWaves));
            break;
        }
      } else if (hasLunchWavesChanges) {
        // Save lunch waves if only waves count changed
        promises.push(updateLunchSlotsMutation.mutateAsync(numLunchWaves));
      }
      
      // Save alphabetic lunch waves if changed
      if (alphabeticLunchWaves !== (school.alphabeticLunchWaves || false)) {
        promises.push(updateSchoolMutation.mutateAsync({
          school: { alphabeticLunchWaves },
        }));
      }
      
      // Process day type changes
      for (const name of dayTypesToCreate) {
        promises.push(createDayTypeMutation.mutateAsync(name));
      }
      for (const { id, name } of dayTypesToUpdate) {
        promises.push(updateDayTypeMutation.mutateAsync({ id, name }));
      }
      for (const id of dayTypesToDelete) {
        promises.push(deleteDayTypeMutation.mutateAsync(id));
      }
      
      // Process period type changes
      for (const name of periodTypesToCreate) {
        promises.push(createPeriodTypeMutation.mutateAsync(name));
      }
      for (const { id, name } of periodTypesToUpdate) {
        promises.push(updatePeriodTypeMutation.mutateAsync({ id, name }));
      }
      for (const id of periodTypesToDelete) {
        promises.push(deletePeriodTypeMutation.mutateAsync(id));
      }
      
      await Promise.all(promises);
      
      // Reset tracking arrays
      setDayTypesToCreate([]);
      setDayTypesToUpdate([]);
      setDayTypesToDelete([]);
      setPeriodTypesToCreate([]);
      setPeriodTypesToUpdate([]);
      setPeriodTypesToDelete([]);
      
      toast.success('Calendar settings saved successfully');
      setEditMode({ ...editMode, calendar: false });
    } catch (error) {
      toast.error('Failed to save calendar settings');
    }
  };

  const saveGeneralSettings = async () => {
    try {
      const schoolUpdates: any = {
        private: isPrivate,
        primaryColor: primaryColor || undefined,
      };

      await updateSchoolMutation.mutateAsync({
        school: schoolUpdates,
        logo: logo || undefined,
      });
      
      // Toast is handled by the mutation's onSuccess handler
      setEditMode({ ...editMode, general: false });
      setLogo(null); // Reset logo file after save
    } catch {
      // Error toast is handled by the mutation's onError handler
    }
  };

  // Cancel edit mode and revert changes
  const cancelBasicInfoEdit = () => {
    setSchoolTitle(school.schoolTitle || '');
    setNickname(school.nickname || '');
    setWebsite(school.website || '');
    setDomains((school.domains as string[]) || []);
    setStudentsCount(school.studentsCount ?? 0);
    setStatus(school.status || 'live');
    setLiteLevel(school.liteLevel || 'L1');
    setLogo(null);
    setLogoPreview(school.logo?.resourceUrl || null);
    setCountry((school.country as 'US' | 'CA') || 'US');
    setState(school.state || '');
    setLat(school.lat?.toString() || '');
    setLng(school.lng?.toString() || '');
    setTimezone(school.timezone || '');
    setEditMode({ ...editMode, basicInfo: false });
  };

  const cancelCalendarEdit = () => {
    setLocalRotationPattern(school.rotationPattern || []);
    setScheduleType(school.scheduleType || '');
    const currentLunchType = school.lunchSettings?.type;
    setLunchType(currentLunchType);
    setNumLunchWaves(
      currentLunchType === 'waves' && school.lunchSettings ? school.lunchSettings.slots.length : 1
    );
    setAlphabeticLunchWaves(school.alphabeticLunchWaves || false);
    
    // Reset day and period types to original state
    setLocalDayTypes(school.dayTypes || []);
    setLocalPeriodTypes(school.periodTypes || []);
    setDayTypesToCreate([]);
    setDayTypesToUpdate([]);
    setDayTypesToDelete([]);
    setPeriodTypesToCreate([]);
    setPeriodTypesToUpdate([]);
    setPeriodTypesToDelete([]);
    setEditingDayTypeId(null);
    setEditingPeriodTypeId(null);
    setIsAddingDayType(false);
    setIsAddingPeriodType(false);
    
    setEditMode({ ...editMode, calendar: false });
  };

  const cancelGeneralEdit = () => {
    setIsPrivate(school.private || false);
    setAlphabeticLunchWaves(school.alphabeticLunchWaves || false);
    setPrimaryColor(school.primaryColor || '');
    setLogo(null);
    setLogoPreview(school.logo?.resourceUrl || null);
    setEditMode({ ...editMode, general: false });
  };

  const handleLunchTypeChange = (newType: 'unit' | 'class' | 'waves') => {
    // In edit mode, only update local state - API calls happen on save
    setLunchType(newType);
  };

  // Day type handlers
  const handleAddDayType = () => {
    if (newDayTypeName.trim()) {
      const cleanName = newDayTypeName.toUpperCase().replace(/[^A-Z0-9/]/g, '');
      if (cleanName && cleanName !== 'X') {
        // Add to local state with temp ID
        const tempId = `temp-${Date.now()}`;
        setLocalDayTypes([...localDayTypes, { id: tempId, name: cleanName }]);
        setDayTypesToCreate([...dayTypesToCreate, cleanName]);
        setNewDayTypeName('');
        setIsAddingDayType(false);
      } else if (cleanName === 'X') {
        toast.error('X is not a valid day type, please use XDAY');
      }
    }
  };

  const handleEditDayType = (id: string, currentName: string) => {
    setEditingDayTypeId(id);
    setEditingDayTypeName(currentName);
  };

  const handleSaveDayType = (id: string) => {
    const cleanName = editingDayTypeName.toUpperCase().replace(/[^A-Z0-9/]/g, '');
    if (cleanName && cleanName !== 'X') {
      // Update local state
      setLocalDayTypes(localDayTypes.map(dt => 
        dt.id === id ? { ...dt, name: cleanName } : dt
      ));
      
      // Track for API update if not a new item
      if (!id.startsWith('temp-')) {
        const existing = dayTypesToUpdate.find(dt => dt.id === id);
        if (existing) {
          setDayTypesToUpdate(dayTypesToUpdate.map(dt => 
            dt.id === id ? { id, name: cleanName } : dt
          ));
        } else {
          setDayTypesToUpdate([...dayTypesToUpdate, { id, name: cleanName }]);
        }
      } else {
        // Update in the create list
        const originalName = localDayTypes.find(dt => dt.id === id)?.name;
        const createIndex = dayTypesToCreate.indexOf(originalName || '');
        if (createIndex >= 0) {
          const newCreate = [...dayTypesToCreate];
          newCreate[createIndex] = cleanName;
          setDayTypesToCreate(newCreate);
        }
      }
      
      setEditingDayTypeId(null);
      setEditingDayTypeName('');
    } else if (cleanName === 'X') {
      toast.error('X is not a valid day type, please use XDAY');
    }
  };

  const handleDeleteDayType = (id: string) => {
    // Remove from local state
    setLocalDayTypes(localDayTypes.filter(dt => dt.id !== id));
    
    // Track for API deletion if not a new item
    if (!id.startsWith('temp-')) {
      setDayTypesToDelete([...dayTypesToDelete, id]);
      // Remove from update list if it was there
      setDayTypesToUpdate(dayTypesToUpdate.filter(dt => dt.id !== id));
    } else {
      // Remove from create list
      const name = localDayTypes.find(dt => dt.id === id)?.name;
      if (name) {
        setDayTypesToCreate(dayTypesToCreate.filter(n => n !== name));
      }
    }
  };

  // Period type handlers
  const handleAddPeriodType = () => {
    if (newPeriodTypeName.trim()) {
      const cleanName = newPeriodTypeName.toUpperCase().replace(/[^A-Z0-9/]/g, '');
      if (cleanName) {
        // Add to local state with temp ID
        const tempId = `temp-${Date.now()}`;
        setLocalPeriodTypes([...localPeriodTypes, { id: tempId, name: cleanName }]);
        setPeriodTypesToCreate([...periodTypesToCreate, cleanName]);
        setNewPeriodTypeName('');
        setIsAddingPeriodType(false);
      }
    }
  };

  const handleEditPeriodType = (id: string, currentName: string) => {
    setEditingPeriodTypeId(id);
    setEditingPeriodTypeName(currentName);
  };

  const handleSavePeriodType = (id: string) => {
    const cleanName = editingPeriodTypeName.toUpperCase().replace(/[^A-Z0-9/]/g, '');
    if (cleanName) {
      // Update local state
      setLocalPeriodTypes(localPeriodTypes.map(pt => 
        pt.id === id ? { ...pt, name: cleanName } : pt
      ));
      
      // Track for API update if not a new item
      if (!id.startsWith('temp-')) {
        const existing = periodTypesToUpdate.find(pt => pt.id === id);
        if (existing) {
          setPeriodTypesToUpdate(periodTypesToUpdate.map(pt => 
            pt.id === id ? { id, name: cleanName } : pt
          ));
        } else {
          setPeriodTypesToUpdate([...periodTypesToUpdate, { id, name: cleanName }]);
        }
      } else {
        // Update in the create list
        const originalName = localPeriodTypes.find(pt => pt.id === id)?.name;
        const createIndex = periodTypesToCreate.indexOf(originalName || '');
        if (createIndex >= 0) {
          const newCreate = [...periodTypesToCreate];
          newCreate[createIndex] = cleanName;
          setPeriodTypesToCreate(newCreate);
        }
      }
      
      setEditingPeriodTypeId(null);
      setEditingPeriodTypeName('');
    }
  };

  const handleDeletePeriodType = (id: string) => {
    // Remove from local state
    setLocalPeriodTypes(localPeriodTypes.filter(pt => pt.id !== id));
    
    // Track for API deletion if not a new item
    if (!id.startsWith('temp-')) {
      setPeriodTypesToDelete([...periodTypesToDelete, id]);
      // Remove from update list if it was there
      setPeriodTypesToUpdate(periodTypesToUpdate.filter(pt => pt.id !== id));
    } else {
      // Remove from create list
      const name = localPeriodTypes.find(pt => pt.id === id)?.name;
      if (name) {
        setPeriodTypesToCreate(periodTypesToCreate.filter(n => n !== name));
      }
    }
  };

  // School year handlers
  const handleAddSchoolYear = () => {
    setEditingSchoolYear(null);
    setShowSchoolYearDialog(true);
  };

  const handleEditSchoolYear = (year: SchoolYear) => {
    setEditingSchoolYear(year);
    setShowSchoolYearDialog(true);
  };

  const handleSchoolYearSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['school', school.schoolName, 'school_years'] });
    await queryClient.invalidateQueries({ queryKey: ['school', school.schoolName] });
    setEditingSchoolYear(null);
  };

  // Helper function to determine if a school year is current based on today's date
  const isCurrentSchoolYear = (year: SchoolYear): boolean => {
    // First check if API marked it as current
    if (year.current) return true;
    
    // Otherwise, check if today falls within the date range
    if (!year.startDate || !year.endDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    
    const startDate = parseISO(year.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = parseISO(year.endDate);
    endDate.setHours(0, 0, 0, 0);
    
    return today >= startDate && today <= endDate;
  };

  // Sort school years: current first, then by end date descending
  const sortedSchoolYears = schoolYears
    ? [...schoolYears].sort((a, b) => {
        const aIsCurrent = isCurrentSchoolYear(a);
        const bIsCurrent = isCurrentSchoolYear(b);
        if (aIsCurrent && !bIsCurrent) return -1;
        if (!aIsCurrent && bIsCurrent) return 1;
        if (!a.endDate || !b.endDate) return 0;
        return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
      })
    : [];

  // Check for changes
  const hasBasicInfoChanges =
    schoolTitle !== school.schoolTitle ||
    nickname !== (school.nickname || '') ||
    website !== (school.website || '') ||
    JSON.stringify(domains) !== JSON.stringify(school.domains || []) ||
    studentsCount !== (school.studentsCount ?? 0) ||
    status !== (school.status || 'live') ||
    liteLevel !== (school.liteLevel || 'L1') ||
    logo !== null;

  const hasLocationChanges =
    country !== (school.country || 'US') ||
    state !== (school.state || '') ||
    lat !== (school.lat?.toString() || '') ||
    lng !== (school.lng?.toString() || '') ||
    timezone !== (school.timezone || '');

  const hasRotationChanges =
    JSON.stringify(localRotationPattern) !== JSON.stringify(school.rotationPattern || []);

  const hasGeneralChanges = isPrivate !== (school.private || false);

  const hasAppearanceChanges = primaryColor !== (school.primaryColor || '') || logo !== null;

  const hasScheduleTypeChanges = scheduleType !== (school.scheduleType || '');

  const hasLunchTypeChanges = lunchType !== school.lunchSettings?.type;

  const hasLunchWavesChanges =
    lunchType === 'waves' &&
    numLunchWaves !== (school.lunchSettings?.type === 'waves' ? school.lunchSettings.slots.length : 1);

  const hasAlphabeticLunchWavesChanges = 
    alphabeticLunchWaves !== (school.alphabeticLunchWaves || false);

  const hasDayTypeChanges = 
    dayTypesToCreate.length > 0 || 
    dayTypesToUpdate.length > 0 || 
    dayTypesToDelete.length > 0 ||
    JSON.stringify(localDayTypes) !== JSON.stringify(school.dayTypes || []);

  const hasPeriodTypeChanges = 
    periodTypesToCreate.length > 0 || 
    periodTypesToUpdate.length > 0 || 
    periodTypesToDelete.length > 0 ||
    JSON.stringify(localPeriodTypes) !== JSON.stringify(school.periodTypes || []);

  return (
    <div className="space-y-6">

      {/* Basic Information & Location - Side by Side */}
      <Collapsible
        open={openSections.basicInfo}
        onOpenChange={(open) => setOpenSections({ ...openSections, basicInfo: open })}
      >
        <Card>
          <CardHeader>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer group">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    School Information
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Basic details, location, and identification
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {openSections.basicInfo && !editMode.basicInfo && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditMode({ ...editMode, basicInfo: true });
                      }}
                      className="hover:bg-muted"
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  {editMode.basicInfo && (hasBasicInfoChanges || hasLocationChanges) && (
                    <Badge variant="secondary" className="shrink-0">Unsaved changes</Badge>
                  )}
                  <div 
                    className="flex items-center justify-center h-8 w-8 rounded-md transition-all"
                    style={{ 
                      backgroundColor: `${schoolPrimaryColor}20`,
                      color: schoolPrimaryColor
                    }}
                  >
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                  </div>
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
            <CardContent>
              {editMode.basicInfo && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-muted/50 rounded-lg border">
                  <p className="text-sm text-muted-foreground flex-1">
                    Editing mode active - make your changes and save when ready
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelBasicInfoEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveBasicInfo}
                    disabled={updateSchoolMutation.isPending || (!hasBasicInfoChanges && !hasLocationChanges)}
                    style={{ 
                      backgroundColor: schoolPrimaryColor,
                      color: getContrastColor(schoolPrimaryColor)
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              )}
              {editMode.basicInfo ? (
              <div className="grid gap-6 md:grid-cols-2">
                {/* Basic Information Column */}
                <div className="space-y-5">
          {/* School ID (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="schoolId">School ID</Label>
            <Input id="schoolId" value={school.schoolName} disabled />
          </div>

          {/* School Title */}
          <div className="space-y-2">
            <Label htmlFor="schoolTitle">
              School Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="schoolTitle"
              value={schoolTitle}
              onChange={(e) => setSchoolTitle(e.target.value)}
              placeholder="Enter school title"
            />
          </div>

          {/* Nickname */}
          <div className="space-y-2">
            <Label htmlFor="nickname">Nickname</Label>
            <Input
              id="nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Optional school nickname"
            />
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          {/* NCES Code (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="ncesCode">NCES Code</Label>
            <Input
              id="ncesCode"
              value={school.ncesCode || ''}
              disabled
            />
          </div>

          {/* Domains */}
          <div className="space-y-2">
            <Label>School Domains</Label>
            <div className="flex gap-2">
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addDomain();
                  }
                }}
                placeholder="example.edu"
              />
              <Button type="button" variant="outline" onClick={addDomain}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {domains.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {domains.map((domain) => (
                  <Badge key={domain} variant="secondary" className="gap-1">
                    {domain}
                    <button
                      type="button"
                      onClick={() => removeDomain(domain)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Enrollment */}
          <div className="space-y-2">
            <Label htmlFor="enrollment">Enrollment</Label>
            <Input
              id="enrollment"
              type="number"
              min="0"
              value={studentsCount}
              onChange={(e) => setStudentsCount(parseInt(e.target.value) || 0)}
            />
          </div>

          {/* School Status */}
          <div className="space-y-2">
            <Label htmlFor="status">School Status</Label>
            <Select value={status} onValueChange={(value: any) => setStatus(value)}>
              <SelectTrigger id="status" className="w-full">
                <SelectValue>
                  <Badge variant={getStatusBadgeVariant(status)}>
                    {SCHOOL_STATUSES.find((s) => s.value === status)?.label || status}
                  </Badge>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {SCHOOL_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    <Badge variant={getStatusBadgeVariant(s.value)}>
                      {s.label}
                    </Badge>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lite Level */}
          {status !== 'live' && (
            <div className="space-y-2">
              <Label htmlFor="liteLevel">Lite Level</Label>
              <Select value={liteLevel} onValueChange={(value: any) => setLiteLevel(value)}>
                <SelectTrigger id="liteLevel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LITE_LEVELS.map((ll) => (
                    <SelectItem key={ll.value} value={ll.value}>
                      {ll.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
                </div>

                {/* Location Information Column */}
                <div className="space-y-5">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                    <MapPin className="h-4 w-4" />
                    Location
                  </h3>
                  
          {/* Country */}
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Select value={country} onValueChange={(value: any) => setCountry(value)}>
              <SelectTrigger id="country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* State/Province */}
          <div className="space-y-2">
            <Label htmlFor="state">
              {country === 'CA' ? 'Province/Territory' : 'State'}
            </Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger id="state">
                <SelectValue placeholder={`Select ${country === 'CA' ? 'province' : 'state'}`} />
              </SelectTrigger>
              <SelectContent>
                {stateOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Latitude */}
          <div className="space-y-2">
            <Label htmlFor="lat">Latitude</Label>
            <Input
              id="lat"
              type="number"
              step="any"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="e.g., 42.3601"
            />
          </div>

          {/* Longitude */}
          <div className="space-y-2">
            <Label htmlFor="lng">Longitude</Label>
            <Input
              id="lng"
              type="number"
              step="any"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="e.g., -71.0589"
            />
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="timezone">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
                </div>
              </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {/* View Mode - Basic Information Column */}
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">School ID</Label>
                        <p className="text-sm font-medium mt-1">{school.schoolName}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">School Title</Label>
                        <p className="text-sm font-medium mt-1">{school.schoolTitle || '—'}</p>
                      </div>
                      {school.nickname && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Nickname</Label>
                          <p className="text-sm font-medium mt-1">{school.nickname}</p>
                        </div>
                      )}
                      {school.website && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Website</Label>
                          <p className="text-sm font-medium mt-1">
                            <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                              {school.website}
                            </a>
                          </p>
                        </div>
                      )}
                      {school.ncesCode && (
                        <div>
                          <Label className="text-xs text-muted-foreground">NCES Code</Label>
                          <p className="text-sm font-medium mt-1">{school.ncesCode}</p>
                        </div>
                      )}
                      {school.domains && school.domains.length > 0 && (
                        <div>
                          <Label className="text-xs text-muted-foreground">School Domains</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {(school.domains as string[]).map((domain) => (
                              <Badge key={domain} variant="secondary">{domain}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs text-muted-foreground">Enrollment</Label>
                        <p className="text-sm font-medium mt-1">{school.studentsCount?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">School Status</Label>
                        <div className="mt-1">
                          <Badge variant={getStatusBadgeVariant(school.status || 'live')}>
                            {SCHOOL_STATUSES.find((s) => s.value === school.status)?.label || school.status}
                          </Badge>
                        </div>
                      </div>
                      {school.status !== 'live' && school.liteLevel && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Lite Level</Label>
                          <p className="text-sm font-medium mt-1">
                            {LITE_LEVELS.find((ll) => ll.value === school.liteLevel)?.label || school.liteLevel}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* View Mode - Location Column */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                      <MapPin className="h-4 w-4" />
                      Location
                    </h3>
                    <div className="space-y-3">
                      {school.country && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Country</Label>
                          <p className="text-sm font-medium mt-1">
                            {COUNTRIES.find((c) => c.value === school.country)?.label || school.country}
                          </p>
                        </div>
                      )}
                      {school.state && (
                        <div>
                          <Label className="text-xs text-muted-foreground">
                            {school.country === 'CA' ? 'Province/Territory' : 'State'}
                          </Label>
                          <p className="text-sm font-medium mt-1">
                            {getStateOptions(school.country as 'US' | 'CA').find((s) => s.value === school.state)?.label || school.state}
                          </p>
                        </div>
                      )}
                      {school.lat && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Latitude</Label>
                          <p className="text-sm font-medium mt-1">{school.lat}</p>
                        </div>
                      )}
                      {school.lng && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Longitude</Label>
                          <p className="text-sm font-medium mt-1">{school.lng}</p>
                        </div>
                      )}
                      {school.timezone && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Timezone</Label>
                          <p className="text-sm font-medium mt-1">{school.timezone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* School Years */}
      <Collapsible
        open={openSections.schoolYears}
        onOpenChange={(open) => setOpenSections({ ...openSections, schoolYears: open })}
      >
        <Card>
          <CardHeader>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer group">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    School Years
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Academic year date ranges
                  </CardDescription>
                </div>
                <div 
                  className="flex items-center justify-center h-8 w-8 rounded-md transition-all"
                  style={{ 
                    backgroundColor: `${schoolPrimaryColor}20`,
                    color: schoolPrimaryColor
                  }}
                >
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  School Years ({sortedSchoolYears.length})
                </h4>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddSchoolYear}
                  className="hover:bg-muted hover:text-foreground"
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Add School Year
                </Button>
              </div>
              
              <div className="space-y-2">
                {isLoadingYears ? (
                  <>
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </>
                ) : sortedSchoolYears.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No school years configured</p>
                ) : (
                  <>
                    {sortedSchoolYears.map((year) => {
                      const isCurrent = isCurrentSchoolYear(year);
                      return (
                        <div
                          key={year.id}
                          className={`group flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors ${
                            isCurrent ? 'border-primary/30 bg-primary/5' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold">
                                {year.startDate && year.endDate
                                  ? `${format(parseISO(year.startDate), 'MMM dd, yyyy')} - ${format(parseISO(year.endDate), 'MMM dd, yyyy')}`
                                  : 'Invalid dates'}
                              </span>
                              {isCurrent && (
                                <Badge variant="default" className="text-[10px] px-1.5 py-0 shrink-0">
                                  Current
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditSchoolYear(year)}
                              title="Edit school year"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Calendar Settings - Rotation Pattern (Hidden for colleges) */}
      {!isCollege && (
        <Collapsible
          open={openSections.calendar}
          onOpenChange={(open) => setOpenSections({ ...openSections, calendar: open })}
        >
          <Card>
            <CardHeader>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between cursor-pointer group">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      Calendar & Schedule Settings
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Rotation pattern, day/period types, lunch configuration, and schedule type
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {openSections.calendar && !editMode.calendar && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditMode({ ...editMode, calendar: true });
                        }}
                        className="hover:bg-muted"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    )}
                    {editMode.calendar && (hasRotationChanges || hasScheduleTypeChanges || hasLunchTypeChanges || hasLunchWavesChanges || hasAlphabeticLunchWavesChanges || hasDayTypeChanges || hasPeriodTypeChanges) && (
                      <Badge variant="secondary" className="shrink-0">Unsaved changes</Badge>
                    )}
                    <div 
                      className="flex items-center justify-center h-8 w-8 rounded-md transition-all"
                      style={{ 
                        backgroundColor: `${schoolPrimaryColor}20`,
                        color: schoolPrimaryColor
                      }}
                    >
                      <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                    </div>
                  </div>
                </div>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
            <CardContent className="space-y-6">
              {editMode.calendar && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border">
                  <p className="text-sm text-muted-foreground flex-1">
                    Editing mode active - make your changes and save when ready
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelCalendarEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveCalendarSettings}
                    disabled={
                      updateRotationMutation.isPending || 
                      updateScheduleTypeMutation.isPending || 
                      updateLunchSlotsMutation.isPending || 
                      updateSchoolMutation.isPending || 
                      createLunchClassMutation.isPending || 
                      deleteLunchClassMutation.isPending ||
                      createDayTypeMutation.isPending ||
                      updateDayTypeMutation.isPending ||
                      deleteDayTypeMutation.isPending ||
                      createPeriodTypeMutation.isPending ||
                      updatePeriodTypeMutation.isPending ||
                      deletePeriodTypeMutation.isPending ||
                      (!hasRotationChanges && !hasScheduleTypeChanges && !hasLunchTypeChanges && !hasLunchWavesChanges && !hasAlphabeticLunchWavesChanges && !hasDayTypeChanges && !hasPeriodTypeChanges)
                    }
                    style={{ 
                      backgroundColor: schoolPrimaryColor,
                      color: getContrastColor(schoolPrimaryColor)
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              )}
              {editMode.calendar ? (
                <div className="space-y-6">
          {/* Rotation Pattern */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Rotation Pattern
              </h4>
              {localRotationPattern.length > 0 && (
                <Badge variant="secondary">{localRotationPattern.length} days</Badge>
              )}
            </div>
            
            {/* Current Pattern */}
            <div className="min-h-[60px] p-4 bg-muted/30 rounded-lg border mb-4">
              {localRotationPattern.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No rotation pattern set</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {localRotationPattern.map((dayType, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-background border hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => removeFromRotation(index)}
                    >
                      <span className="font-medium text-sm">{dayType}</span>
                      <div className="h-4 w-px bg-border" />
                      <X className="h-3 w-3 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Day Type Buttons */}
            {localDayTypes && localDayTypes.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wide">
                  Available Day Types
                </p>
                <div className="flex flex-wrap gap-2">
                  {localDayTypes.map((dayType) => (
                    <Button
                      key={dayType.id}
                      variant="outline"
                      size="sm"
                      onClick={() => addToRotation(dayType.name)}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      {dayType.name}
                    </Button>
                  ))}
                  <div className="flex-1" />
                  <Button variant="default" size="sm" onClick={autofillRotation}>
                    Autofill All
                  </Button>
                </div>
              </div>
            )}
            
            {(!localDayTypes || localDayTypes.length === 0) && (
              <div className="text-sm text-muted-foreground">
                No day types configured. Add day types below first.
              </div>
            )}
          </div>

          {/* Schedule Type & Lunch Settings - Side by Side */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Schedule Type */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Schedule Type
                </h4>
              </div>
              <Select value={scheduleType} onValueChange={setScheduleType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select schedule type" />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_TYPES.map((st) => (
                    <SelectItem key={st.value} value={st.value}>
                      {st.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

          {/* Lunch Settings */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Lunch Settings
            </h4>
            <RadioGroup
              value={lunchType || 'unit'}
              onValueChange={(value) => handleLunchTypeChange(value as 'unit' | 'class' | 'waves')}
              className="space-y-1"
            >
              {LUNCH_TYPES.map((lt) => (
                <div key={lt.value} className="flex items-center space-x-3 py-2 px-3 rounded-md hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value={lt.value} id={`lunch-${lt.value}`} className="h-4 w-4" />
                  <Label htmlFor={`lunch-${lt.value}`} className="font-normal cursor-pointer flex-1">
                    {lt.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            
            {lunchType === 'waves' && (
              <div className="mt-4 p-4 rounded-lg bg-muted/30 border space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="numWaves" className="text-sm font-medium">
                      Number of Lunch Waves
                    </Label>
                  </div>
                  <Input
                    id="numWaves"
                    type="number"
                    min="1"
                    max="10"
                    value={numLunchWaves}
                    onChange={(e) => setNumLunchWaves(parseInt(e.target.value) || 1)}
                    className="w-32"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-sm">Alphabetic Lunch Waves</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Use A, B, C instead of 1, 2, 3
                    </p>
                  </div>
                  <Switch
                    checked={alphabeticLunchWaves}
                    onCheckedChange={setAlphabeticLunchWaves}
                  />
                </div>
              </div>
            )}
          </div>
          </div>

          {/* Day Types & Period Types - Side by Side */}
          <div className="grid gap-6 md:grid-cols-2">
          {/* Day Types */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Day Types ({localDayTypes?.length || 0})
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsAddingDayType(true)}
                disabled={isAddingDayType}
                className="hover:bg-muted hover:text-foreground"
              >
                <Plus className="mr-1 h-3 w-3" />
                Add Day Type
              </Button>
            </div>
            
            <div className="space-y-2">
              {localDayTypes && localDayTypes.length > 0 ? (
                <>
                  {localDayTypes.map((dayType) => (
                    <div
                      key={dayType.id}
                      className="flex items-center gap-2 p-2 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    >
                      {editingDayTypeId === dayType.id ? (
                        <>
                          <Input
                            value={editingDayTypeName}
                            onChange={(e) => setEditingDayTypeName(e.target.value)}
                            className="flex-1 h-8"
                            autoFocus
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveDayType(dayType.id);
                              } else if (e.key === 'Escape') {
                                setEditingDayTypeId(null);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSaveDayType(dayType.id)}
                            disabled={updateDayTypeMutation.isPending}
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingDayTypeId(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 font-medium">{dayType.name}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditDayType(dayType.id, dayType.name)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteDayType(dayType.id)}
                            disabled={deleteDayTypeMutation.isPending}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">No day types configured</p>
              )}
              
              {isAddingDayType && (
                <div className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30">
                  <Input
                    value={newDayTypeName}
                    onChange={(e) => setNewDayTypeName(e.target.value)}
                    placeholder="e.g., A, B, C"
                    className="flex-1 h-8"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddDayType();
                      } else if (e.key === 'Escape') {
                        setIsAddingDayType(false);
                        setNewDayTypeName('');
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handleAddDayType}
                    disabled={!newDayTypeName.trim() || createDayTypeMutation.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsAddingDayType(false);
                      setNewDayTypeName('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Period Types */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Period Types ({localPeriodTypes?.length || 0})
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsAddingPeriodType(true)}
                disabled={isAddingPeriodType}
                className="hover:bg-muted hover:text-foreground"
              >
                <Plus className="mr-1 h-3 w-3" />
                Add Period Type
              </Button>
            </div>
            
            <div className="space-y-2">
              {localPeriodTypes && localPeriodTypes.length > 0 ? (
                <>
                  {localPeriodTypes.map((periodType) => (
                    <div
                      key={periodType.id}
                      className="flex items-center gap-2 p-2 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    >
                      {editingPeriodTypeId === periodType.id ? (
                        <>
                          <Input
                            value={editingPeriodTypeName}
                            onChange={(e) => setEditingPeriodTypeName(e.target.value)}
                            className="flex-1 h-8"
                            autoFocus
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSavePeriodType(periodType.id);
                              } else if (e.key === 'Escape') {
                                setEditingPeriodTypeId(null);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSavePeriodType(periodType.id)}
                            disabled={updatePeriodTypeMutation.isPending}
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingPeriodTypeId(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 font-medium">{periodType.name}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditPeriodType(periodType.id, periodType.name)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeletePeriodType(periodType.id)}
                            disabled={deletePeriodTypeMutation.isPending}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">No period types configured</p>
              )}
              
              {isAddingPeriodType && (
                <div className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30">
                  <Input
                    value={newPeriodTypeName}
                    onChange={(e) => setNewPeriodTypeName(e.target.value)}
                    placeholder="e.g., 1, 2, 3"
                    className="flex-1 h-8"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddPeriodType();
                      } else if (e.key === 'Escape') {
                        setIsAddingPeriodType(false);
                        setNewPeriodTypeName('');
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handleAddPeriodType}
                    disabled={!newPeriodTypeName.trim() || createPeriodTypeMutation.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsAddingPeriodType(false);
                      setNewPeriodTypeName('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* View Mode - Rotation Pattern */}
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                      Rotation Pattern
                    </h4>
                    {school.rotationPattern && school.rotationPattern.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {school.rotationPattern.map((dayType, index) => (
                          <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                            {dayType}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No rotation pattern set</p>
                    )}
                  </div>

                  {/* View Mode - Schedule Type & Lunch Settings */}
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                        Schedule Type
                      </h4>
                      <p className="text-sm font-medium">
                        {school.scheduleType ? SCHEDULE_TYPES.find(st => st.value === school.scheduleType)?.label || school.scheduleType : '—'}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                        Lunch Settings
                      </h4>
                      <p className="text-sm font-medium">
                        {school.lunchSettings?.type ? LUNCH_TYPES.find(lt => lt.value === school.lunchSettings?.type)?.label || school.lunchSettings?.type : 'Unit (default)'}
                      </p>
                      {school.lunchSettings?.type === 'waves' && school.lunchSettings.slots && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm text-muted-foreground">
                            {school.lunchSettings.slots.length} lunch wave{school.lunchSettings.slots.length !== 1 ? 's' : ''}
                          </p>
                          {school.alphabeticLunchWaves && (
                            <Badge variant="secondary" className="text-xs">Alphabetic (A, B, C)</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* View Mode - Day Types & Period Types */}
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                        Day Types ({school.dayTypes?.length || 0})
                      </h4>
                      {school.dayTypes && school.dayTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {school.dayTypes.map((dayType) => (
                            <Badge key={dayType.id} variant="outline" className="text-sm px-3 py-1">
                              {dayType.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No day types configured</p>
                      )}
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                        Period Types ({school.periodTypes?.length || 0})
                      </h4>
                      {school.periodTypes && school.periodTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {school.periodTypes.map((periodType) => (
                            <Badge key={periodType.id} variant="outline" className="text-sm px-3 py-1">
                              {periodType.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No period types configured</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
        </Collapsible>
      )}

      {/* General Settings & Appearance - Side by Side */}
      <Collapsible
        open={openSections.general}
        onOpenChange={(open) => setOpenSections({ ...openSections, general: open })}
      >
        <Card>
          <CardHeader>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer group">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    General Settings & Appearance
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Privacy settings and visual customization
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {openSections.general && !editMode.general && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditMode({ ...editMode, general: true });
                      }}
                      className="hover:bg-muted"
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  {editMode.general && (hasGeneralChanges || hasAppearanceChanges) && (
                    <Badge variant="secondary" className="shrink-0">Unsaved changes</Badge>
                  )}
                  <div 
                    className="flex items-center justify-center h-8 w-8 rounded-md transition-all"
                    style={{ 
                      backgroundColor: `${schoolPrimaryColor}20`,
                      color: schoolPrimaryColor
                    }}
                  >
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                  </div>
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
            <CardContent>
              {editMode.general && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-muted/50 rounded-lg border">
                  <p className="text-sm text-muted-foreground flex-1">
                    Editing mode active - make your changes and save when ready
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelGeneralEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveGeneralSettings}
                    disabled={updateSchoolMutation.isPending || (!hasGeneralChanges && !hasAppearanceChanges)}
                    style={{ 
                      backgroundColor: schoolPrimaryColor,
                      color: getContrastColor(schoolPrimaryColor)
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              )}
              {editMode.general ? (
              <div className="grid gap-6 md:grid-cols-2">
                {/* General Settings Column */}
                <div className="space-y-5">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                    <Settings className="h-4 w-4" />
                    General Settings
                  </h3>
                  <div className="flex items-center justify-between gap-4 p-4 rounded-lg border">
                    <div className="flex-1">
                      <p className="font-medium">Private School</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Mark this school as private</p>
                    </div>
                    <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
                  </div>
                </div>

                {/* Appearance Column */}
                <div className="space-y-5">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                    <Palette className="h-4 w-4" />
                    Appearance
                  </h3>
                  
                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <Label htmlFor="logo">School Logo</Label>
                    <div className="flex items-center gap-4">
                      {logoPreview && (
                        <div className="h-16 w-16 rounded-md border overflow-hidden shrink-0">
                          <img
                            src={logoPreview}
                            alt="School logo"
                            className="h-full w-full object-contain"
                          />
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium mb-3 block">
                      Primary Color
                    </Label>
                    {primaryColor && (
                      <div className="mb-3 p-3 rounded-lg border bg-muted/30 flex items-center gap-3">
                        <div
                          className="h-10 w-10 rounded-md shrink-0 ring-2 ring-border/50"
                          style={{ backgroundColor: primaryColor }}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {COLOR_SWATCHES.find(c => c.value === primaryColor)?.label || 'Custom Color'}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      {COLOR_SWATCHES.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setPrimaryColor(color.value)}
                          className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-all hover:scale-105 ${
                            primaryColor === color.value
                              ? 'border-foreground ring-2 ring-ring/50'
                              : 'border-border hover:border-foreground/50'
                          }`}
                        >
                          <div
                            className="h-6 w-6 rounded-md shrink-0 ring-1 ring-border/50"
                            style={{ backgroundColor: color.value }}
                          />
                          <span className="text-xs font-medium text-left">{color.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {/* View Mode - General Settings Column */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                      <Settings className="h-4 w-4" />
                      General Settings
                    </h3>
                    <div>
                      <Label className="text-xs text-muted-foreground">Private School</Label>
                      <p className="text-sm font-medium mt-1">
                        {school.private ? (
                          <Badge variant="secondary">Private</Badge>
                        ) : (
                          <Badge variant="outline">Public</Badge>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* View Mode - Appearance Column */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                      <Palette className="h-4 w-4" />
                      Appearance
                    </h3>
                    {school.logo?.resourceUrl && (
                      <div>
                        <Label className="text-xs text-muted-foreground">School Logo</Label>
                        <div className="mt-2 h-16 w-16 rounded-md border overflow-hidden">
                          <img
                            src={school.logo.resourceUrl}
                            alt="School logo"
                            className="h-full w-full object-contain"
                          />
                        </div>
                      </div>
                    )}
                    {school.primaryColor && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Primary Color</Label>
                        <div className="mt-2 flex items-center gap-3">
                          <div
                            className="h-10 w-10 rounded-md shrink-0 ring-2 ring-border/50"
                            style={{ backgroundColor: school.primaryColor }}
                          />
                          <div>
                            <p className="text-sm font-medium">
                              {COLOR_SWATCHES.find(c => c.value === school.primaryColor)?.label || 'Custom Color'}
                            </p>
                            <p className="text-xs text-muted-foreground">{school.primaryColor}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Security & Access */}
      {Array.isArray(school.regexBlock) && school.regexBlock.length > 0 && (
        <Collapsible
          open={openSections.security}
          onOpenChange={(open) => setOpenSections({ ...openSections, security: open })}
        >
          <Card>
            <CardHeader>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between cursor-pointer group">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Security & Access
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Access control and security settings
                    </CardDescription>
                  </div>
                  <div 
                    className="flex items-center justify-center h-8 w-8 rounded-md transition-all"
                    style={{ 
                      backgroundColor: `${schoolPrimaryColor}20`,
                      color: schoolPrimaryColor
                    }}
                  >
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                  </div>
                </div>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
              <CardContent>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Regex Block Patterns
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Email patterns that are blocked from registering
                  </p>
                  <div className="space-y-2">
                    {school.regexBlock.map((pattern, index) => (
                      <code
                        key={index}
                        className="block rounded-md bg-muted px-3 py-2 text-sm font-mono"
                      >
                        {pattern}
                      </code>
                    ))}
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Cohort Types */}
      {school.cohortTypes && school.cohortTypes.length > 0 && (
        <Collapsible
          open={openSections.cohorts}
          onOpenChange={(open) => setOpenSections({ ...openSections, cohorts: open })}
        >
          <Card>
            <CardHeader>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between cursor-pointer group">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Cohort Types
                    </CardTitle>
                    <CardDescription className="mt-1">Available cohort types for this school</CardDescription>
                  </div>
                  <div 
                    className="flex items-center justify-center h-8 w-8 rounded-md transition-all"
                    style={{ 
                      backgroundColor: `${schoolPrimaryColor}20`,
                      color: schoolPrimaryColor
                    }}
                  >
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                  </div>
                </div>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {school.cohortTypes.map((type) => (
                    <Badge key={type} variant="secondary">
                      {type}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* School Year Dialog */}
      <SchoolYearDialog
        open={showSchoolYearDialog}
        onOpenChange={setShowSchoolYearDialog}
        schoolYear={editingSchoolYear}
        schoolId={school.schoolName}
        schoolPrimaryColor={schoolPrimaryColor}
        onSuccess={handleSchoolYearSuccess}
      />
    </div>
  );
}
